export const assistantArticles = [
    {
        id: 'assistant.overview',
        title: 'Ask Costed — AI Assistant',
        category: 'AI Assistant',
        order: 1,
        summary: 'Chat with an AI that knows your workspace — ask questions and make changes',
        content: `
**Ask Costed** is a built-in AI assistant that can read your workspace data and make changes for you — without leaving Costed.app. Open it with the **✦ Ask** button at the bottom-right of any screen.

Ask it things in plain English, for example:

- *"What's my price for 32MPa concrete?"*
- *"List my jobs in Tahmoor"*
- *"Build an assembly for a concrete slab"*
- *"Prepare an RFQ to price waterproofing for job 1480"*

It looks up real data using your catalogue, jobs, suppliers and BOQ totals — it never makes up prices or codes.

**Every change is confirmed.** When the assistant wants to create or change something, it shows an **Approve / Decline** card first — nothing is written until you approve it.

**Speak instead of typing:** click the **🎤 microphone** next to the message box to dictate (Chrome/Edge). Your words appear in the box live — review and edit before sending.

**Show it a picture:** paste a screenshot straight into the message box (Ctrl+V), or click the **📎 paperclip** to attach up to 3 images — a plan detail, a supplier's price list photo, an error on screen — and ask about it. Images are shrunk before sending. (Requires a vision-capable model: Claude, GPT or Gemini — DeepSeek can't view images.)
    `,
        expandableSections: [
            {
                title: 'Slash commands — ready-made workflows',
                content: `
Type **/** in the message box to open the command menu — ready-made workflows that tell the assistant exactly how to approach common estimating jobs:

| Command | What it does |
|---|---|
| **/recipe** \`<item>\` | Build or audit an assembly in full trade scope (found / missing / verify table first) |
| **/rfq** \`<scope>\` | Prepare an RFQ — items + suggested suppliers; you review and send it |
| **/reprice** \`<job or items>\` | Find zero/stale prices and propose updates |
| **/find-gaps** \`<job no>\` | Audit a job's BOQ for missing scope, zero prices, missing suppliers |
| **/add-to-boq** \`<items>\` | Add items on a new load so you can raise the PO |
| **/job** \`<job no>\` | Job health summary — contract position, budget vs actual |
| **/help** \`<question>\` | Answer from these help articles, with links |

Use **↑ ↓** to choose, **Enter** or **Tab** to select, then type the details and send. The assistant still confirms any change before it runs.
        `,
            },
            {
                title: 'Setting up an AI model (required)',
                content: `
The assistant runs on an AI model that you supply a key for (bring-your-own-key — usage is billed by the provider, not by Costed.app).

Go to **Settings → Developer & AI → AI Keys & Assistant → AI Assistant Models**:

1. Choose a **provider** — Claude (Anthropic), ChatGPT (OpenAI), Gemini (Google) or DeepSeek.
2. The **model** auto-fills a sensible default — edit it if you like.
3. Optionally add a **label** (e.g. "Fast", "Cheap").
4. Paste that provider's **API key** (the form links to each provider's console), click **Test**, then **Add Model**.

You can save **several models** and switch between them. The key is encrypted and never shown again.

**Note on DeepSeek:** DeepSeek requires prepaid credit before any call works — a new key with no balance returns "Insufficient Balance". Add funds in the DeepSeek console first. Claude / OpenAI / Google typically include trial credit.
        `,
            },
            {
                title: 'Switching models in the panel',
                content: `
If you've saved more than one model, a **dropdown appears in the panel header** (top-right, next to the new-chat and close buttons).

- Pick any saved model, or **Default (Claude)** to use your invoice-extraction Claude key.
- Your choice is remembered between sessions.
- You can switch mid-conversation — the next message uses the newly selected model.

This is separate from **invoice extraction**, which always uses your Claude key.
        `,
            },
            {
                title: 'What it can do',
                content: `
**Look things up (instant, no confirmation):**

- Search the catalogue and fetch item / assembly detail
- List and open jobs (with BOQ summary totals)
- List suppliers

**Make changes (always confirmed first):**

- Create catalogue items and assemblies
- Add or change prices
- Archive / restore catalogue items (purchase-order-locked items are protected)
- **Prepare a Request for Quote** to get current supplier pricing

The assistant works entirely within your workspace and your user permissions — it can't do anything you couldn't do yourself.
        `,
            },
            {
                title: 'Getting current pricing with an RFQ',
                content: `
Need fresh supplier pricing — or pricing for a trade/task that isn't in your price file yet? Ask the assistant to **prepare an RFQ**.

- It creates the RFQ, adds the items, and attaches the suppliers you choose — in one approval.
- Items that **aren't in your catalogue** are fine: they go in as ad-hoc lines (just a description, quantity and unit). You don't have to create catalogue entries first.
- The assistant **prepares the RFQ but does not send it.** It gives you the RFQ number; open the **RFQ screen** to review and send to suppliers.
- When quotes come back, use the RFQ comparison and **apply prices** to write the winning quote back to your catalogue — closing the loop on those new items.
        `,
            },
        ],
        keywords: ['ai', 'assistant', 'ask costed', 'chat', 'chatgpt', 'claude', 'gemini', 'deepseek', 'model', 'rfq', 'api key', 'byok'],
        relatedArticles: ['catalogue.overview', 'settings.overview', 'assistant.activity'],
    },
    {
        id: 'assistant.activity',
        title: 'AI Activity & Review',
        category: 'AI Assistant',
        order: 2,
        route: '/ai-activity',
        summary: 'Every change made by AI shows up here first — review it, open the record, or dismiss it',
        content: `
The AI Activity page is the audit trail for changes made by AI on your workspace — whether through the **Ask Costed** assistant or a **connected AI tool** (via MCP). Nothing AI does is hidden: every data change lands here so a person can check it.

Open it from the sidebar under **System → AI Activity**, or click the **✨ badge** in the top bar — the badge shows how many entries are waiting for review.

There are two tabs:

- **Needs review** — AI-prepared changes nobody has checked yet (marked with a ✨)
- **All** — the full history, including entries already reviewed or dismissed
    `,
        expandableSections: [
            {
                title: 'Reading an Entry',
                content: `
Each row shows:

- **What happened** — a plain-English summary of the change
- **Source chip** — **Assistant** (made through the Ask Costed chat) or **Connector** (made by an external AI tool connected via MCP)
- **Who and when** — the user the AI was acting for, and how long ago
- **Status** — pending entries carry a ✨; checked entries show *Reviewed* or *Dismissed*
        `,
            },
            {
                title: 'Reviewing Changes',
                content: `
For each pending entry you can:

- **Open** — jump straight to the record that was created or changed, so you can inspect it (and fix it there if needed)
- **Mark reviewed** — records that a person has checked the change; the record itself stays exactly as-is
- **Dismiss** — hides the entry from the review queue without recording a review

Marking reviewed or dismissing never modifies the underlying record — it is bookkeeping for the review queue only. If a change is wrong, open the record and correct it the normal way.
        `,
            },
        ],
        keywords: ['ai activity', 'review', 'audit', 'pending', 'needs review', 'mcp', 'connector', 'assistant changes', 'ai audit trail'],
        relatedArticles: ['assistant.overview'],
        screenshots: [
            {
                src: 'general/ai-activity.png',
                alt: 'AI Activity page with the review queue',
                caption: 'The AI Activity page — every AI-made change with its source, actor, and review status',
            },
        ],
    },
];
